import React, { useEffect, useRef, useState } from "react";
import { motion } from "framer-motion";
import { ArrowRight, Eye, EyeOff, Lock } from "lucide-react";
import { Logo } from "./Logo";
import { PREVIEW_LOCK_ENABLED, PREVIEW_PASSWORD, grantAccess, isUnlocked } from "../utils/access";
import { lockScroll } from "../utils/scrollLock";
type PasswordGateProps = {
  children: React.ReactNode;
};

/** Soft access gate for shared preview links. Renders the site only once the password matches. */
export function PasswordGate({
  children
}: PasswordGateProps) {
  const [unlocked, setUnlocked] = useState(() => !PREVIEW_LOCK_ENABLED || isUnlocked());
  const [value, setValue] = useState("");
  const [reveal, setReveal] = useState(false);
  const [error, setError] = useState(false);
  const inputRef = useRef<HTMLInputElement>(null);
  useEffect(() => {
    if (unlocked) return;
    const release = lockScroll();
    inputRef.current?.focus();
    return release;
  }, [unlocked]);
  if (unlocked) return <>{children}</>;
  const onSubmit = (event: React.FormEvent<HTMLFormElement>) => {
    event.preventDefault();
    if (value.trim() === PREVIEW_PASSWORD) {
      grantAccess();
      setUnlocked(true);
      return;
    }
    setError(true);
    setValue("");
    inputRef.current?.focus();
  };
  return <main className="relative grid min-h-[100svh] w-full place-items-center overflow-hidden bg-[#050808] px-5 text-white" data-id="element-328" data-mp="35g3evs5ylj63">
      <div aria-hidden="true" className="absolute inset-0 film-grain opacity-[0.18]" data-id="element-329" data-mp="3advdt650u5js" />
      <div aria-hidden="true" className="absolute inset-0 vignette" data-id="element-330" data-mp="3fcpv23ncbm1s" />
      <div aria-hidden="true" className="absolute left-1/2 top-1/2 h-[520px] w-[520px] -translate-x-1/2 -translate-y-1/2 rounded-full bg-[#54f6c6]/[0.07] blur-[130px]" data-id="element-331" data-mp="3k4q2si9njj8x" />

      <div aria-hidden="true" className="pointer-events-none absolute inset-5 hidden md:block" data-id="element-332" data-mp="3t565hpikmmcq">
        <span className="absolute left-0 top-0 h-7 w-px bg-white/20" data-id="element-333" data-mp="1yuxtaj22ncf" />
        <span className="absolute left-0 top-0 h-px w-7 bg-white/20" data-id="element-334" data-mp="1z4n0vwa0qh2" />
        <span className="absolute right-0 top-0 h-7 w-px bg-white/20" data-id="element-335" data-mp="1y317pfpigse7" />
        <span className="absolute right-0 top-0 h-px w-7 bg-white/20" data-id="element-336" data-mp="1u0xxsvihpvv1" />
        <span className="absolute bottom-0 left-0 h-7 w-px bg-white/20" data-id="element-337" data-mp="1tyl79tyo2d6d" />
        <span className="absolute bottom-0 left-0 h-px w-7 bg-white/20" data-id="element-338" data-mp="27tdvdgsmh1ok" />
        <span className="absolute bottom-0 right-0 h-7 w-px bg-white/20" data-id="element-339" data-mp="2301fpw5x857y" />
        <span className="absolute bottom-0 right-0 h-px w-7 bg-white/20" data-id="element-340" data-mp="2hph5p9xmo6ml" />
      </div>

      <motion.div className="relative w-full max-w-[440px] rounded-[20px] border border-white/10 bg-[#0a0f0e] p-7 sm:p-9" initial={{
      opacity: 0,
      y: 22
    }} animate={{
      opacity: 1,
      y: 0
    }} transition={{
      duration: 0.7,
      ease: [0.16, 1, 0.3, 1]
    }} data-id="element-341" data-mp="2cqw16ybcf0sc">
        <span aria-hidden="true" className="absolute inset-x-0 top-0 h-px bg-gradient-to-r from-transparent via-[#54f6c6]/60 to-transparent" data-id="element-342" data-mp="2lpra4i5ruozc" />

        <div className="flex items-center justify-between gap-4" data-id="element-343" data-mp="recly5sesxlp">
          <Logo className="h-7 w-[62px]" data-id="element-344" data-mp="330whlmgd6dsm" />
          <span className="flex items-center gap-2 font-mono text-[11px] uppercase tracking-[0.18em] text-white/45" data-id="element-345" data-mp="331xy46g5fshq">
            <Lock className="h-3.5 w-3.5" aria-hidden="true" data-id="element-346" data-mp="37zhmhz6k9lne" />
            Private preview
          </span>
        </div>

        <h1 className="mt-9 text-[clamp(1.6rem,5vw,2.1rem)] font-semibold leading-[1.06] tracking-[-0.045em] text-white" data-id="element-347" data-mp="2u6i3hn7kl9b3">
          This preview is <span className="text-[#54f6c6]" data-id="element-348" data-mp="2k9vjt1qzcoan">locked</span>
        </h1>
        <p className="mt-4 text-sm leading-7 text-white/60" data-id="element-349" data-mp="2kcrhqq2tvcz3">
          Enter the access password to view the portfolio. Ask Syed Muhammad Zeeshan if you need it.
        </p>

        <form onSubmit={onSubmit} className="mt-8" data-id="element-350" data-mp="2bbjledjvgm7n">
          <label htmlFor="access-password" className="font-mono text-[11px] uppercase tracking-[0.18em] text-white/45" data-id="element-351" data-mp="2fasj1ti1948h">
            Password
          </label>

          <div className="mt-3 flex items-center gap-2 rounded-xl border border-white/15 bg-white/[0.04] px-4 focus-within:border-[#54f6c6]/60" data-id="element-352" data-mp="9g9cyb7t7vct">
            <input ref={inputRef} id="access-password" name="access-password" type={reveal ? "text" : "password"} value={value} autoComplete="current-password" aria-invalid={error} aria-describedby={error ? "access-error" : undefined} onChange={event => {
            setValue(event.target.value);
            if (error) setError(false);
          }} className="min-h-12 w-full bg-transparent text-sm text-white outline-none placeholder:text-white/30" placeholder="Enter password" data-id="element-353" data-mp="3kyyvg0b63y2s" />
            <button type="button" onClick={() => setReveal(current => !current)} aria-label={reveal ? "Hide password" : "Show password"} className="grid h-11 w-11 shrink-0 place-items-center rounded-xl text-white/50 transition-colors hover:text-white focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[#54f6c6]" data-id="element-354" data-mp="2nayi95koxbfs">
              {reveal ? <EyeOff className="h-4 w-4" aria-hidden="true" data-id="element-355" data-mp="xtjezraqg38z" /> : <Eye className="h-4 w-4" aria-hidden="true" data-id="element-356" data-mp="xs8d5x0ul5ki" />}
            </button>
          </div>

          <p id="access-error" role="status" aria-live="polite" className="mt-3 min-h-5 font-mono text-[11px] uppercase tracking-[0.14em] text-[#ff5f56]" data-id="element-357" data-mp="12tflbx6wk6rp">
            {error ? "Incorrect password, try again" : ""}
          </p>

          <button type="submit" disabled={!value.trim()} className="group mt-5 inline-flex min-h-12 w-full items-center justify-center gap-3 rounded-xl bg-[#54f6c6] px-6 text-sm font-medium uppercase tracking-[0.08em] text-[#05100d] transition-opacity hover:opacity-90 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[#54f6c6] focus-visible:ring-offset-2 focus-visible:ring-offset-[#0a0f0e] disabled:cursor-not-allowed disabled:opacity-40" data-id="element-358" data-mp="a2dz5c4slayo">
            Enter site
            <ArrowRight className="h-4 w-4 transition-transform duration-300 group-hover:translate-x-1" aria-hidden="true" data-id="element-359" data-mp="3kh0envmw5cau" />
          </button>
        </form>

        <p className="mt-7 border-t border-white/10 pt-5 font-mono text-[11px] uppercase tracking-[0.14em] text-white/35" data-id="element-360" data-mp="3gdubwxyzm394">
          Syed Muhammad Zeeshan · Motion · VFX · CGI
        </p>
      </motion.div>
    </main>;
}